import React, { useEffect, useState, useRef } from "react"
import { Line } from "react-chartjs-2"
import { Spin, Typography, DatePicker, Row, Col, Table, Select, Space } from "antd"
import dayjs from "dayjs"
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js"
import "chartjs-adapter-date-fns"
import annotationPlugin from "chartjs-plugin-annotation"

const { Text, Title: ATitle } = Typography
const { RangePicker } = DatePicker

// Register Chart.js components including the Annotation plugin
ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  annotationPlugin
)

// Define table columns (for the orders in the selected month)
const columns = [
  {
    title: "Series",
    dataIndex: "docPrefix",
    width: "15%",
    key: "docPrefix",
  },
  {
    title: "Client",
    dataIndex: ["client", "client"],
    width: "70%",
    key: "client",
  },
  {
    title: "Revenue",
    dataIndex: ["totalData", "totalNetoSum"],
    key: "totalNetoSum",
    sorter: (a, b) => {
      const revenueA = a.totalData?.totalNetoSum || 0
      const revenueB = b.totalData?.totalNetoSum || 0
      return revenueB - revenueA
    },
    defaultSortOrder: "ascend",
    width: "15%",
    render: (value) => (
      <Text style={{ color: "#52c41a" }}>
        €
        {Number(value || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Text>
    ),
  },
]

export default function Chart({ invoices, loading }) {
  // State for the month range (using dayjs objects)
  const [monthRange, setMonthRange] = useState([dayjs("2018-01", "YYYY-MM"), dayjs()])
  const [chartData, setChartData] = useState({ datasets: [] })
  // State to store the clicked (selected) month as a Date
  const [selectedMonthDate, setSelectedMonthDate] = useState(null)
  // State to store the orders (invoices) for the clicked month
  const [selectedMonthOrders, setSelectedMonthOrders] = useState([])
  // New state for the revenue threshold (red line)
  const [threshold, setThreshold] = useState(40000)

  // Ref to the chart instance for click events.
  const chartRef = useRef(null)

  // When invoices or monthRange change, compute the chart data.
  useEffect(() => {
    const startDate = monthRange[0].startOf("month").toDate()
    const endDate = monthRange[1].endOf("month").toDate()

    // Create monthly buckets for the range.
    const monthlyTotals = {}
    let current = new Date(startDate)
    while (current <= endDate) {
      const key = dayjs(current).format("YYYY-MM")
      monthlyTotals[key] = 0
      current.setMonth(current.getMonth() + 1)
    }

    // Aggregate invoice totals into these monthly buckets.
    invoices.forEach((invoice) => {
      const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
      if (invoiceDate >= startDate && invoiceDate <= endDate) {
        const key = dayjs(invoiceDate).format("YYYY-MM")
        if (key in monthlyTotals) {
          monthlyTotals[key] += invoice.totalData?.totalNetoSum || 0
        }
      }
    })

    // Create data points sorted chronologically.
    const dataPoints = Object.keys(monthlyTotals)
      .sort()
      .map((key) => ({
        x: dayjs(key, "YYYY-MM").toDate(), // first day of the month
        y: monthlyTotals[key],
      }))

    // Compute point styling based on selected month.
    const pointBackgroundColors = dataPoints.map((dataPoint) => {
      const dpMonth = dayjs(dataPoint.x).format("YYYY-MM")
      const selected = selectedMonthDate ? dayjs(selectedMonthDate).format("YYYY-MM") : null
      return dpMonth === selected ? "#ff4d4f" : "#1890ff"
    })
    const pointRadii = dataPoints.map((dataPoint) => {
      const dpMonth = dayjs(dataPoint.x).format("YYYY-MM")
      const selected = selectedMonthDate ? dayjs(selectedMonthDate).format("YYYY-MM") : null
      return dpMonth === selected ? 8 : 4
    })

    setChartData({
      datasets: [
        {
          label: "Revenue",
          data: dataPoints,
          borderColor: "#1890ff",
          backgroundColor: "#1890ff33",
          fill: true,
          tension: 0.3,
          pointBackgroundColor: pointBackgroundColors,
          pointRadius: pointRadii,
        },
      ],
    })
  }, [invoices, monthRange, selectedMonthDate])

  // Update the chart when the threshold changes.
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update()
    }
  }, [threshold])

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 20 }}>
        <Spin tip="Loading chart..." />
      </div>
    )
  }

  // Chart configuration options with the annotation plugin for the threshold line.
  const options = {
    animation: { duration: 0 },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? "pointer" : "default"
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "month", tooltipFormat: "MMM yyyy" },
        title: { display: true, text: "Month" },
        ticks: {
          color: "#666",
          callback: function (value) {
            return this.getLabelForValue(value)
          },
        },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Revenue" },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      annotation: {
        annotations: {
          thresholdLine: {
            type: "line",
            yMin: threshold,
            yMax: threshold,
            borderColor: "red",
            borderWidth: 2,
            label: {
              enabled: true,
              content: threshold.toLocaleString("en-US", { minimumFractionDigits: 0 }),
              backgroundColor: "red",
              position: "start",
            },
          },
        },
      },
    },
  }

  // When the RangePicker (month mode) changes, update the monthRange state.
  const handleRangeChange = (dates) => {
    if (dates) {
      setMonthRange(dates)
    }
  }

  // Handle chart click events: determine which data point was clicked.
  const handleChartClick = (event) => {
    if (!chartRef.current) return

    const elements = chartRef.current.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      false
    )
    if (elements.length > 0) {
      const firstElement = elements[0]
      const datasetIndex = firstElement.datasetIndex
      const index = firstElement.index
      const clickedDataPoint = chartData.datasets[datasetIndex].data[index]
      const clickedMonth = clickedDataPoint.x // Date object for the first day of the month
      setSelectedMonthDate(clickedMonth)

      // Filter invoices for the clicked month.
      const ordersForMonth = invoices.filter((invoice) => {
        const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
        return (
          invoiceDate.getFullYear() === clickedMonth.getFullYear() &&
          invoiceDate.getMonth() === clickedMonth.getMonth()
        )
      })
      setSelectedMonthOrders(ordersForMonth)
      console.log("Clicked Month Orders:", ordersForMonth)
    }
  }

  // Generate options for the threshold Select.
  const thresholdOptions = Array.from({ length: Math.floor(150000 / 5000) + 1 }).map((_, index) => {
    const value = index * 10000
    return (
      <Select.Option key={value} value={value}>
        {value.toLocaleString("en-US")}
      </Select.Option>
    )
  })

  return (
    <Row gutter={[15, 15]}>
      <Col span={16}>
        <div
          style={{
            background: "white",
            padding: "20px 20px 80px 20px",
            borderRadius: 15,
            height: "500px",
          }}>
          <div>
            <Space direction="vertical">
              <Space style={{ marginLeft: 15 }}>
                <Text strong>Revenue Filter</Text>
                <RangePicker
                  picker="month"
                  style={{ marginLeft: 15 }}
                  defaultValue={monthRange}
                  onChange={handleRangeChange}
                />
              </Space>
              <Space style={{ marginLeft: 15 }}>
                <Text>Threshold: </Text>
                <Select value={threshold} onChange={setThreshold} style={{ width: 120 }}>
                  {thresholdOptions}
                </Select>
              </Space>
            </Space>
          </div>
          <Line data={chartData} options={options} ref={chartRef} onClick={handleChartClick} />
        </div>
      </Col>
      <Col span={8}>
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: 15,
            height: "500px",
            overflowY: "auto",
          }}>
          {selectedMonthOrders.length === 0 ? (
            <>
              <ATitle level={2}>Nothing to show</ATitle>
              <Text>Select a month in chart...</Text>
            </>
          ) : (
            <Table
              columns={columns}
              dataSource={selectedMonthOrders}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              rowKey="id"
            />
          )}
        </div>
      </Col>
    </Row>
  )
}
